import { createAction, createSlice } from '@reduxjs/toolkit';
import panels from '@/constants/panels';
import { hidePanelLoading, showPanelLoading, changeTableRow, hidePanel, getTableDataSuccess, clearFilter, setFilter, getLogoForAccount, updateTable } from '@/pages/shared/shared.reducer';
// import {setCurrentSiteId} from '@/pages/dashboard/dashboard.reducer'
import { runAccountMutation, runAccountQuery } from '@/pages/accounts/account.query';
import { isUnauthorizedError, notifyUnlessUnauthorized } from '@/pages/shared/rtk-query-helpers';
import { openErrorNotification, openSuccessNotification } from '@/notification';
import { CHANGES_ARE_SAVED } from '@/constants/constant.string';
import { config } from '@/config';
import { setAccountDashboardSettingsForAccount } from '@/pages/dashboard/dashboard.slice';
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer';

const VALUE_SET = "ACCOUNT_VALUE_SET";
const CLEAR_ACCOUNT_ITEM = "CLEAR_ACCOUNT_ITEM";
const SET_ACCOUNTS_COLUMNS = "SET_ACCOUNTS_COLUMNS";

const initialState = {
  item: {}, // selected table item,
  record: { 
    allowedManualUserCreation: true, 
    permissionsGrantedManually: true 
  }, // loaded table item data
  editRecord: { 
    allowedManualUserCreation: true, 
    permissionsGrantedManually: true 
  },
  detailsLoading: false,
}

// Replaces immutability-helper `update.set`: assigns a dotted path, creating missing objects.
const setPathValue = (draft, path, value) => {
    const segments = String(path).split('.');
    let target = draft;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];

        if (target[segment] === null || typeof target[segment] !== 'object') {
            target[segment] = {};
        }

        target = target[segment];
    }

    target[segments[segments.length - 1]] = value;
};

const normalizeAccountDashboardSettings = (accountCERFilters = []) =>
  accountCERFilters.map((filter) => ({
    cerRecordTypeId: filter.cerRecordTypeId,
    dashboardName: filter.dashboardName,
    dashboardIdTitle: filter.dashboardIdTitle,
    sortOrder: filter.sortOrder,
    active: filter.active,
    icon: filter.icon,
  }));

const syncAccountDashboardSettings = (dispatch, accountId, accountCERFilters) => {
  if (!accountId) {
    return;
  }

  dispatch(setAccountDashboardSettingsForAccount({
    accountId,
    dashboards: normalizeAccountDashboardSettings(accountCERFilters),
  }));
};

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const setValue = createAction(VALUE_SET, (key, value) => ({
  payload: { key, value }
}));

export const clearData = createAction(CLEAR_ACCOUNT_ITEM);

export const setColumns = createAction(SET_ACCOUNTS_COLUMNS, (tableId, value) => ({
  payload: { tableId, value }
}));

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setValue, (draft, action) => {
        setPathValue(draft, `${action.payload.key}`, action.payload.value);
      })
      .addCase(clearData, () => initialState)
      .addCase(setColumns, (draft, action) => {
        setPathValue(draft, `${action.payload.tableId}.columns`, action.payload.value);
      });
  }
});

const accountReducer = accountSlice.reducer;

export const clearAccount = (accountId) => {
  return (dispatch) => {
    dispatch(getTableDataSuccess(`accountUsersTable-${accountId}`, { data: [], totalItems: 0 }));
    dispatch(getTableDataSuccess('accountUsersSitesTable', { data: [], totalItems: 0 }));
    dispatch(clearData());
  }
}

export const changeAccountValue = (key, value) => {
  return (dispatch) => {
    dispatch(setValue(`editRecord.${key}`, value));

    if (key === 'accountCERFilters') {
      dispatch((innerDispatch, getState) => {
        const accountId = getState().account.editRecord.accountId || getState().account.record.accountId;
        syncAccountDashboardSettings(innerDispatch, accountId, value);
      });
    }
  }
}

export const loadAccount = (id) => {
  return (dispatch) => {
    dispatch(showPanelLoading(panels.account));
    dispatch(setValue('detailsLoading', true));
    runAccountQuery(dispatch, 'getAccount', id)
      .then((data) => {
        dispatch(setValue('record', data));
        dispatch(setValue('editRecord', data));
        syncAccountDashboardSettings(dispatch, data.accountId, data.accountCERFilters);
        dispatch(hidePanelLoading(panels.account));
      })
      .catch(error => {
        if (!isUnauthorizedError(error)) {
          dispatch(hidePanelLoading(panels.account));
          openErrorNotification(`Can't load account details. Please reload page`);
        }
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setValue('detailsLoading', false));
        }, config.LOADING_DELAY)
      });
  }
}

export const deleteAccount = (item, isFilterActive) => {
  return (dispatch) => {
    dispatch(setValue('detailsLoading', true));

    runAccountMutation(dispatch, 'deleteAccount', item.accountId)
      .then((data) => {
        if (!data.active) {
          openSuccessNotification('Account is disabled');
          dispatch(setFilter('accountUsersSitesTable', 'page', 1))
        }
        else {
          openErrorNotification("Not disabled");
        }

        dispatch(changeTableRow('accountTable', data, 'accountId', isFilterActive == "true"));
        dispatch(setFilter('dashboardTable', 'key', Math.floor(Math.random() * Math.floor(2500))));
        dispatch(updateTable('dashboardTable'));
        dispatch(setValue('editRecord', data));
        dispatch(setValue('record', data));
        dispatch(setValue('item', data));
        syncAccountDashboardSettings(dispatch, data.accountId, data.accountCERFilters);
        // dispatch(setCurrentSiteId(0))
      })
      .catch(error => {
        notifyUnlessUnauthorized(error, `Error. Can't disable account`);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setValue('detailsLoading', false));
        }, config.LOADING_DELAY)
      })
  }
}

export const undeleteAccount = (item, isFilterActive) => {
  return (dispatch) => {
    dispatch(setValue('detailsLoading', true));

    runAccountMutation(dispatch, 'undeleteAccount', item.accountId)
      .then(data => {
        if (data.active) {
          openSuccessNotification('Account is reactivated');
        }
        else {
          openErrorNotification("Not reactivated");
        }

        dispatch(changeTableRow('accountTable', data, 'accountId', isFilterActive == "false"));
        dispatch(setFilter('dashboardTable', 'key', Math.floor(Math.random() * Math.floor(2500))));
        dispatch(setValue('editRecord', data));
        dispatch(setValue('record', data));
        dispatch(setValue('item', data));
        syncAccountDashboardSettings(dispatch, data.accountId, data.accountCERFilters);
      })
      .catch(error => {
        notifyUnlessUnauthorized(error, `Error. Can't reactivate account`);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setValue('detailsLoading', false));
        }, config.LOADING_DELAY)
      })
  }
}

export const saveAccountDetails = (model, needClose) => {
  return (dispatch) => {
    const isNewAccount = !model.accountId || model.accountId === 0;
    dispatch(setValue('detailsLoading', true));
    runAccountMutation(dispatch, 'saveAccountDetails', model)
      .then(async data => {
        let siteListRefreshFailed = false;
        try {
          await dispatch(dashboardApi.endpoints.fetchSiteList.initiate(undefined, { forceRefetch: true })).unwrap();
        } catch {
          siteListRefreshFailed = true;
        }

        dispatch(getLogoForAccount(data.accountId, 'Light'));
        dispatch(getLogoForAccount(data.accountId, 'Dark'));
        dispatch(setValue('mainDetails', data));
        syncAccountDashboardSettings(dispatch, data.accountId, data.accountCERFilters ?? model.accountCERFilters);
        openSuccessNotification(CHANGES_ARE_SAVED);
        if (needClose) {
          dispatch(hidePanel('accountPanel'));
        }
        else {
          dispatch(setValue('item', data));
          if (isNewAccount) {
            // For new accounts, load full details to populate all tabs
            runAccountQuery(dispatch, 'getAccount', data.accountId)
              .then((fullData) => {
                dispatch(setValue('record', fullData));
                dispatch(setValue('editRecord', fullData));
                syncAccountDashboardSettings(dispatch, fullData.accountId, fullData.accountCERFilters);
                dispatch(clearFilter('accountHistoryTable'));
              })
              .catch(() => {
                // Fallback to basic response data if full load fails
                dispatch(setValue('record', data));
                dispatch(setValue('editRecord', data));
                syncAccountDashboardSettings(dispatch, data.accountId, data.accountCERFilters ?? model.accountCERFilters);
                dispatch(clearFilter('accountHistoryTable'));
              });
          } else {
            // For existing accounts, use the response data directly
            dispatch(setValue('record', data));
            dispatch(setValue('editRecord', data));
            syncAccountDashboardSettings(dispatch, data.accountId, data.accountCERFilters ?? model.accountCERFilters);
            dispatch(clearFilter('accountHistoryTable'));
          }
        }
        dispatch(changeTableRow('accountTable', data, 'accountId'));
        if (siteListRefreshFailed) {
          openErrorNotification('Account saved, but site settings could not be refreshed. Reload the page before creating a job.');
        }
      })
      .catch(error => {
        notifyUnlessUnauthorized(error, `Error. Can't save account`);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setValue('detailsLoading', false));
        }, config.LOADING_DELAY);
      })
  }
}


export default accountReducer;